import {useTranslation} from 'react-i18next';
import config from '../../../../../app/config';
import React, {useEffect} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {emitChangedWait, obsAges, obsStatuses, obsTypes} from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import AgyleDictionaryField from '../../../../../components/ui/selector/agyleDictionaryField';
import {useTheme} from '@mui/material/styles';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import {FormLabel} from '@mui/material';
import {obsStyles} from '../obsStyle';

function ObsNeuroBestVerbalResponse(props) {
    const classes = obsStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const {type, ageRangeYears} = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [bestVerbalResponse, setBestVerbalResponse] = React.useState(props.bestVerbalResponse ?? props.response);
    const [tracheostomy, setTracheostomy] = React.useState(props.tracheostomy);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    const [bestVerbalResponses, setBestVerbalResponses] = React.useState([]);

    useEffect(() => {
        let eyes = [];
        if (type === obsTypes.neuro) {
            if (ageRangeYears === obsAges._16_99) {
                eyes = [
                    {
                        id: '5',
                        description: t('Oriented'),
                        score: 5,
                        // default: true,
                    },
                    {
                        id: '4',
                        description: t('Confused'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Inappropriate words'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Incomprehensible Sounds'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._5_16) {
                eyes = [
                    {
                        id: '5',
                        description: t('Orientated'),
                        score: 5,
                        // default: true,
                    },
                    {
                        id: '4',
                        description: t('Confused'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Inappropriate words'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Incomprehensible Sounds'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('Silent'),
                        score: 1,
                    },
                ];
            } else if (ageRangeYears === obsAges._1_5) {
                eyes = [
                    {
                        id: '5',
                        description: t(
                            'Age Appropriate Vocalisation/Oriented/Interacts',
                        ),
                        score: 5,
                        // default: true,
                    },
                    {
                        id: '4',
                        description: t('Cries/Irritable'),
                        score: 4,
                    },
                    {
                        id: '3',
                        description: t('Cries to Pain'),
                        score: 3,
                    },
                    {
                        id: '2',
                        description: t('Moans to Pain'),
                        score: 2,
                    },
                    {
                        id: '1',
                        description: t('None'),
                        score: 1,
                    },
                ];
            }
        }
        setBestVerbalResponses(eyes);
    }, [type]);

    function setDefaults() {
        let def = _.find(bestVerbalResponses, (item) => !!item.default);
        // if (def) {
        setBestVerbalResponse(def);
        // }
    }

    useEffect(() => {
        if (!tracheostomy && !bestVerbalResponse) {
            setDefaults();
        }
    }, [bestVerbalResponses, tracheostomy]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
        setTracheostomy(false);
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    useEffect(() => {
        const checkValue =
            (bestVerbalResponse && status === obsStatuses.new) ||
            (status !== obsStatuses.completed &&
                status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [tracheostomy, bestVerbalResponse, status]);

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    response: bestVerbalResponse,
                    tracheostomy,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };

    useEffect(() => {
        emitChanged();
    }, [
        status,
        bestVerbalResponse,
        tracheostomy,
        score,
        accepted,
        acceptDate,
    ]);

    const updateScore = () => {
        let newScore = undefined;
        if (
            status === obsStatuses.notRecorded ||
            status === obsStatuses.patientRefused ||
            status === obsStatuses.equipment
        ) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (bestVerbalResponse) {
                newScore = bestVerbalResponse.score;
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, bestVerbalResponse, tracheostomy]);

    const onResponseChange = (value) => {
        setBestVerbalResponse(value);
        setTracheostomy(false);
        clearAccepted();
    };

    const onTracheostomyChange = (value) => {
        setTracheostomy(value);
        if (tracheostomy) {
            setDefaults();
        } else {
            setBestVerbalResponse({
                id: '0',
                description: t('T'),
                score: undefined,
            });
        }
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    let scoreColor = 'neuro';
    if (score >= 4) {
        scoreColor = 'neuroLow';
    } else if (score === 3) {
        scoreColor = 'neuroMedium';
    } else if (score >= 1) {
        scoreColor = 'neuroHigh';
    }

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    <Grid item xs={12} className={classes.divRow}>
                        <AgyleBlockSwitch
                            onChange={onTracheostomyChange}
                            value={tracheostomy}
                            customLabels={{
                                yes: t('Yes'),
                                no: t('No'),
                            }}
                            style={{
                                backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                            }}
                        />

                        <FormLabel className={classes.label} style={{marginLeft: 5}}>
                            {t('Tracheostomy')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={12} className={classes.component}>
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{marginBottom: 15}}
                        >
                            <Grid item xs={12}>
                                <FormLabel className={classes.label}>
                                    {t('Best Verbal Response')}
                                </FormLabel>
                            </Grid>

                            <Grid item xs={12}>
                                <AgyleDictionaryField
                                    onChange={(_, value) => onResponseChange(value)}
                                    options={bestVerbalResponses}
                                    value={bestVerbalResponse}
                                    getOptionLabel={(option) => option.description}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    extended
                                    noBorder={false}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {renderAcceptSection({
                        classes,
                        t,
                        score,
                        status,
                        renderScore: (
                            <div className={clsx([classes.scoreBadge, scoreColor])}>
                                {tracheostomy && t('T')}
                                {!tracheostomy && _.isUndefined(score) && (
                                    <span>&nbsp;</span>
                                )}
                                {!tracheostomy && _.isNumber(score) && score}
                            </div>
                        ),
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: !tracheostomy && !bestVerbalResponse,
                        hideStatuses: true,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsNeuroBestVerbalResponse.propTypes = {
    type: PropTypes.oneOf([
        obsTypes.news,
        obsTypes.pews,
        obsTypes.neuro,
    ]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    bestVerbalResponse: PropTypes.object,
    tracheostomy: PropTypes.bool,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsNeuroBestVerbalResponse.defaultProps = {
    type: obsTypes.neuro,
    status: obsStatuses.new,
    score: 0,
    bestVerbalResponse: null,
    tracheostomy: false,
    accepted: false,
    acceptDate: null,
};
// ObsNeuroBestVerbalResponse.renderSectionScore = ({
export const renderNeuroBestVerbalResponseSectionScore = ({
                                                              classes,
                                                              t,
                                                              section,
                                                              obs,
                                                              status,
                                                              score,
                                                              accepted,
                                                          }) => {
    let color = 'neuro';
    if (score >= 4) {
        color = 'neuroLow';
    } else if (score === 3) {
        color = 'neuroMedium';
    } else if (score >= 1) {
        color = 'neuroHigh';
    }

    return ((obs?.tracheostomy || score) &&
        <div
            className={clsx([
                classes.sectionScore,
                color,
                accepted && classes.sectionScoreSelected,
            ])}>
            {obs.tracheostomy && t('T')}
            {!obs.tracheostomy && _.isUndefined(score) && (
                <span>&nbsp;</span>
            )}
            {!obs.tracheostomy && _.isNumber(score) && score}
        </div>
    );
};

export default React.memo(ObsNeuroBestVerbalResponse);